<template>
    <v-container
        fluid
        class="pa-2 pr-0 pb-1"
        :style="$vuetify.display.mobile ? 'width: 100vw;' : ''"
    >
        <template v-if="!$vuetify.display.mobile">
            <v-window v-model="globalStep">
                <v-window-item :value="0">
                    <v-row no-gutters v-if="loading">
                        <v-col cols="12" class="d-flex justify-center align-center">
                            <v-progress-circular indeterminate color="primary" />
                        </v-col>
                    </v-row>
                    <template v-else>
                        <v-row no-gutters class="mt-2 mb-4 d-flex align-center">
                            <v-col cols="6" class="d-flex align-center">
                                <v-btn-back @click.stop="back()"> </v-btn-back>
                                <div class="ml-4">
                                    <h3>DAS2</h3>
                                    <p>{{ formatDate(startDate) + " - " + formatDate(endDate) }}</p>
                                </div>
                            </v-col>
                            <v-col cols="6" class="d-flex justify-end pr-4">
                                <v-btn
                                    rounded
                                    class="mr-1"
                                    elevation="0"
                                    :disabled="globalStep <= 0"
                                    v-if="globalStep >= 1"
                                    @click="globalStep--"
                                    size="small"
                                    >Précédent</v-btn
                                >
                                <v-btn
                                    rounded
                                    elevation="0"
                                    v-if="globalStep < 1"
                                    @click="globalStep++"
                                    size="small"
                                    >Suivant</v-btn
                                >
                            </v-col>
                        </v-row>
                        <v-row no-gutters>
                            <v-col cols="12" class="pr-2">
                                <DasTwoDeclaringCard :declaring="declaring" />
                                <DasTwoBeneficiariesCard
                                    :items="beneficiaries"
                                    :loading="loading"
                                    :companyId="companyId"
                                    :startDate="startDate"
                                    :endDate="endDate"
                                    :isIncomingOutgoingPaymentAccountingType="
                                        accountingType === 'INCOMING_OUTGOING_PAYMENT'
                                    "
                                />
                            </v-col>
                        </v-row>
                    </template>
                </v-window-item>
                <v-window-item :value="1">
                    <v-row no-gutters>
                        <v-col cols="12">
                            <DeclarationManager
                                v-if="globalStep === 1"
                                :propsCerfaNumber="cerfaNumber"
                                :propsStartDate="startDate"
                                :propsEndDate="endDate"
                                :assistDasTwoMode="true"
                                :assistDasTwoDeclaring="declaring"
                                :assistDasTwoBeneficiaries="beneficiariesToAdd"
                                @back="globalStep--"
                            >
                            </DeclarationManager>
                        </v-col>
                    </v-row>
                </v-window-item>
            </v-window>
        </template>
    </v-container>
</template>

<script lang="ts">
import { Apollo } from "@/decorators";
import DAS_TWO from "@/graphql/declaration/DasTwo.gql";
import DeclarationManager from "./DeclarationManager.vue";
import DasTwoDeclaringCard from "./DasTwoDeclaringCard.vue";
import DasTwoBeneficiariesCard from "./DasTwoBeneficiariesCard.vue";
import { Component, Vue, toNative, Prop } from "vue-facing-decorator";
import { DasTwoBeneficiary, DasTwoDeclaring } from "@/services/declaration/declarationServices";
import moment from "moment-timezone";

@Component({
    components: {
        DeclarationManager,
        DasTwoDeclaringCard,
        DasTwoBeneficiariesCard,
    },
})
export class DasTwo extends Vue {
    @Prop({ default: null }) companyId: string;
    @Prop({ default: null }) cerfaNumber: string;
    @Prop({ default: null }) startDate: string;
    @Prop({ default: null }) endDate: string;

    // general
    globalStep = 0;

    back() {
        this.$router.push({ name: "taxForms" });
    }

    formatDate(date: string): string {
        return moment(date).format("DD/MM/YYYY") || "";
    }

    // declaring
    declaring: DasTwoDeclaring = {
        address: "",
        city: "",
        postalCode: "",
        siret: "",
        companyName: "",
        closingDate: "",
        signatoryName: "",
        signatoryEmail: "",
        signatoryPhone: "",
        ape: "",
        postOfficeDistributor: "",
        number: "",
        btq: "",
        street: "",
        inseeCode: "",
        getCityInseeCode: async () => {},
    };

    accountingType: string = null;

    async beforeMount() {
        const selectedCompany: SelectedCompany = this.$store?.state?.selected_company;
        const selectedAccountingPeriod: { end: string } =
            this.$store?.state?.accountingPeriod?.selectedAccountingPeriod;
        this.declaring = new DasTwoDeclaring(
            selectedCompany?.address,
            selectedCompany?.city,
            selectedCompany?.postalCode,
            selectedCompany?.siret,
            selectedCompany?.name,
            moment(selectedAccountingPeriod?.end).format("DDMM"),
            selectedCompany?.activities?.[0]?.name,
        );
        await this.declaring.getCityInseeCode();
        this.accountingType = selectedCompany?.profile?.accountingType;
    }

    // beneficiaries
    @Apollo({
        query: DAS_TWO,
        variables() {
            return {
                companyId: this.companyId,
                startDate: this.startDate,
                endDate: this.endDate,
            };
        },
        skip() {
            return !this.companyId || !this.startDate || !this.endDate;
        },
        async specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data?.dasTwo?.result) {
                    const promises = data.dasTwo.result.map(async (item: any) => {
                        const newItem = new DasTwoBeneficiary(
                            item.address,
                            item.city,
                            item.debit,
                            item.thirdPartyCompanyName,
                            item.postalCode,
                            item.siret,
                            item.id,
                            item.accountableAccountName,
                            item.legalForm,
                        );
                        await newItem.getCityInseeCode();
                        return newItem;
                    });
                    const items = await Promise.all(promises);
                    this.beneficiaries.push(...items);
                }
            }
        },
    })
    beneficiaries: DasTwoBeneficiary[] = [];

    get loading() {
        if (this.$apollo.queries.beneficiaries) {
            return this.$apollo.queries.beneficiaries.loading;
        }
        return false;
    }

    get beneficiariesToAdd() {
        return this.beneficiaries.filter((beneficiary) => beneficiary.toAdd);
    }
}

type SelectedCompany = {
    activities: any[];
    id: string;
    name: string;
    siret: string;
    address: string;
    city: string;
    postalCode: string;
    profile: {
        accountingType: string;
    };
};

export default toNative(DasTwo);
</script>
